// v2 bird — a perky songbird: a plump warm breast under a coloured back, a
// capped head with a stout beak, swept folded wing panels and a long tilted
// tail. Three deterministic plumages — robin, bluebird and a yellow finch —
// each a back/breast/cap trio. Ported 1:1 from age-of-mcp's v2/bird.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const PLUMAGES = [
  { back: "#6e5a44", breast: "#d97a4a", cap: "#544335", beak: "#e8b04e" },
  { back: "#4a6a9c", breast: "#d8c8a8", cap: "#3e5a88", beak: "#3a3a40" },
  { back: "#7c7c4c", breast: "#e8c84e", cap: "#6a6a3e", beak: "#caa040" },
];

function birdParts(v: number): Part[] {
  const c = PLUMAGES[v % PLUMAGES.length]!;
  const parts: Part[] = [
    // body — back colour, plump through the middle
    { geometry: new THREE.IcosahedronGeometry(0.036, 0), color: c.back,
      position: [0.004, 0.034, 0], scale: [1.45, 1.05, 0.95] },
    // warm breast pushed forward and low
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.breast,
      position: [0.028, 0.027, 0], scale: [1.0, 0.95, 0.92] },
    // head with a darker cap
    { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.back,
      position: [0.05, 0.058, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.016, 0), color: c.cap,
      position: [0.052, 0.069, 0], scale: [1.25, 0.7, 1.1] },
    // stout beak
    { geometry: new THREE.ConeGeometry(0.0075, 0.026, 4), color: c.beak,
      position: [0.076, 0.057, 0], rotation: [0, 0, -Math.PI / 2] },
    // eyes
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: "#16120e",
      position: [0.059, 0.064, 0.0125] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: "#16120e",
      position: [0.059, 0.064, -0.0125] },
    // folded wing panels swept back along the flanks
    { geometry: new THREE.BoxGeometry(0.055, 0.006, 0.028), color: c.cap,
      position: [-0.004, 0.042, 0.031], rotation: [0, -0.42, 0.14] },
    { geometry: new THREE.BoxGeometry(0.055, 0.006, 0.028), color: c.cap,
      position: [-0.004, 0.042, -0.031], rotation: [0, 0.42, 0.14] },
    // long tilted tail
    { geometry: new THREE.BoxGeometry(0.05, 0.005, 0.02), color: c.cap,
      position: [-0.054, 0.036, 0], rotation: [0, 0, 0.2] },
  ];
  return parts;
}

export const birdV2 = defineModel({
  id: "bird-v2",
  batchId: "fable",
  name: "Bird (v2)",
  tags: ["creature","bird","low-poly","age-of-mcp","fable"],
  summary: "Low-poly bird.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.07,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => birdParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => birdParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => birdParts(2) },
  ],
  animations: [],
});
